import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Thunk para validar código de acceso
export const validateAccessCode = createAsyncThunk(
  'auth/validateAccessCode',
  async (code, { rejectWithValue }) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000/api'}/access-codes/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Código inválido');
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  isAuthenticated: false,
  userCode: '',
  userData: null,
  eventData: null,
  accessCodeToken: null, // ← Nuevo: JWT específico para access codes
  loading: false,
  error: null,
  isInitialized: false,
  sessionStartTime: null, // Tiempo de inicio de sesión
  sessionTimeout: 10 * 60 * 1000, // 10 minutos en milisegundos
};

// Función para cargar estado inicial desde localStorage
const loadInitialState = () => {
  try {
    const savedAuth = localStorage.getItem('theaterAuth');
    const savedUserCode = localStorage.getItem('theaterUserCode');
    const savedUserData = localStorage.getItem('theaterUserData');
    const savedEventData = localStorage.getItem('theaterEventData');
    const savedAccessCodeToken = localStorage.getItem('theaterAccessCodeToken'); // ← Nuevo
    const savedSessionStartTime = localStorage.getItem('theaterSessionStartTime');
    
    if (savedAuth === 'true' && savedUserCode && savedAccessCodeToken) { // ← Verificar token
      // Verificar si la sesión ha expirado
      const sessionStartTime = savedSessionStartTime ? parseInt(savedSessionStartTime) : null;
      const currentTime = Date.now();
      const sessionTimeout = 10 * 60 * 1000; // 10 minutos
      
      if (sessionStartTime && (currentTime - sessionStartTime) > sessionTimeout) {
        // Sesión expirada, limpiar datos
        console.log('Sesión expirada, limpiando datos...');
        localStorage.removeItem('theaterAuth');
        localStorage.removeItem('theaterUserCode');
        localStorage.removeItem('theaterUserData');
        localStorage.removeItem('theaterEventData');
        localStorage.removeItem('theaterAccessCodeToken'); // ← Limpiar token
        localStorage.removeItem('theaterSessionStartTime');
        return {
          ...initialState,
          isInitialized: false,
        };
      }
      
      return {
        ...initialState,
        isAuthenticated: true,
        userCode: savedUserCode,
        userData: savedUserData ? JSON.parse(savedUserData) : null,
        eventData: savedEventData ? JSON.parse(savedEventData) : null,
        accessCodeToken: savedAccessCodeToken, // ← Nuevo
        sessionStartTime: sessionStartTime,
        isInitialized: true,
      };
    }
  } catch (error) {
    console.error('Error loading auth state from localStorage:', error);
    // Limpiar datos corruptos
    localStorage.removeItem('theaterAuth');
    localStorage.removeItem('theaterUserCode');
    localStorage.removeItem('theaterUserData');
    localStorage.removeItem('theaterEventData');
    localStorage.removeItem('theaterAccessCodeToken'); // ← Limpiar token
    localStorage.removeItem('theaterSessionStartTime');
  }
  
  // Si no hay datos guardados, mantener isInitialized en false
  // hasta que se complete la carga de configuración
  return {
    ...initialState,
    isInitialized: false, // Cambiar a false para esperar la configuración
  };
};

const authSlice = createSlice({
  name: 'auth',
  initialState: loadInitialState(),
  reducers: {
    // Login automático para acceso libre
    setFreeAccess: (state) => {
      state.isAuthenticated = true;
      state.userCode = 'ACCESO_LIBRE';
      state.error = null;
      state.sessionStartTime = Date.now();
      
      // Guardar en localStorage
      localStorage.setItem('theaterAuth', 'true');
      localStorage.setItem('theaterUserCode', 'ACCESO_LIBRE');
      localStorage.setItem('theaterSessionStartTime', state.sessionStartTime.toString());
    },
    
    // Logout
    logout: (state) => {
      state.isAuthenticated = false;
      state.userCode = '';
      state.userData = null;
      state.eventData = null;
      state.accessCodeToken = null; // ← Limpiar token
      state.error = null;
      state.sessionStartTime = null;
      
      // Limpiar localStorage
      localStorage.removeItem('theaterAuth');
      localStorage.removeItem('theaterUserCode');
      localStorage.removeItem('theaterUserData');
      localStorage.removeItem('theaterEventData');
      localStorage.removeItem('theaterAccessCodeToken'); // ← Limpiar token
      localStorage.removeItem('theaterSessionStartTime');
    },
    
    // Limpiar error
    clearError: (state) => {
      state.error = null;
    },

    // Marcar como inicializado
    setInitialized: (state) => {
      state.isInitialized = true;
    },

    // Verificar expiración de sesión
    checkSessionExpiration: (state) => {
      if (state.isAuthenticated && state.userCode !== 'ACCESO_LIBRE' && state.sessionStartTime) {
        const currentTime = Date.now();
        const timeElapsed = currentTime - state.sessionStartTime;
        
        if (timeElapsed > state.sessionTimeout) {
          // Sesión expirada, hacer logout automático
          state.isAuthenticated = false;
          state.userCode = '';
          state.userData = null;
          state.eventData = null;
          state.accessCodeToken = null; // ← Limpiar token
          state.sessionStartTime = null;
          
          // Limpiar localStorage
          localStorage.removeItem('theaterAuth');
          localStorage.removeItem('theaterUserCode');
          localStorage.removeItem('theaterUserData');
          localStorage.removeItem('theaterEventData');
          localStorage.removeItem('theaterAccessCodeToken'); // ← Limpiar token
          localStorage.removeItem('theaterSessionStartTime');
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Validar código de acceso
      .addCase(validateAccessCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(validateAccessCode.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.userCode = action.payload.code || action.payload.accessCode?.code;
        
        state.accessCodeToken = action.payload.access_token;
        
        // Extraer userData de accessCode.student si existe
        let userData = null;
        if (action.payload.userData) {
          userData = action.payload.userData;
        } else if (action.payload.accessCode?.student) {
          userData = {
            id: action.payload.accessCode.student.id,
            name: action.payload.accessCode.student.name,
            email: action.payload.accessCode.student.email,
          };
        }
        
        // Extraer eventData si existe
        let eventData = null;
        if (action.payload.eventData) {
          eventData = action.payload.eventData;
        } else if (action.payload.accessCode?.event) {
          eventData = {
            id: action.payload.accessCode.event.id,
            title: action.payload.accessCode.event.title,
            date: action.payload.accessCode.event.eventDate.toISOString().split('T')[0],
            time: action.payload.accessCode.event.eventTime,
            isOpenSale: action.payload.accessCode.event.isOpenSale,
            maxTicketsPerCode: action.payload.accessCode.event.maxTicketsPerCode,
          };
        }
        
        state.userData = userData;
        state.eventData = eventData;
        state.error = null;
        
        // Establecer tiempo de inicio de sesión para códigos de acceso
        state.sessionStartTime = Date.now();
        
        localStorage.setItem('theaterAuth', 'true');
        localStorage.setItem('theaterUserCode', state.userCode);
        localStorage.setItem('theaterAccessCodeToken', state.accessCodeToken);
        localStorage.setItem('theaterSessionStartTime', state.sessionStartTime.toString());
        if (userData) {
          localStorage.setItem('theaterUserData', JSON.stringify(userData));
        }
        if (eventData) {
          localStorage.setItem('theaterEventData', JSON.stringify(eventData));
        }
      })
      .addCase(validateAccessCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setFreeAccess, logout, clearError, setInitialized, checkSessionExpiration } = authSlice.actions;
export default authSlice.reducer; 